var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import Trigger from './Trigger';
import Button from '../../../button';
import Input from '../../../input';
import Select from '../../../select';
import { ConfigContext } from '../../../config-provider';
import ControlCloseCircle from '../../../_util/bonreeIcons/ControlCloseCircle';
import ControlOpen from '../../../_util/bonreeIcons/ControlOpen';
import LiveTail from '../../../_util/bonreeIcons/LiveTail';
import { useLocaleReceiver } from '../../../locale-provider/LocaleReceiver';
import enUS from '../../locale/en_US';
import Panel from './Panel';
import { computedDuration, GlobalPickerType, parseShortcutValue, computeShortcutOptions, addGlobalMouseDownEvent, getTargetFromEvent, elementsContains, 
// datesToValue,
durationText, generateRefreshOptions, valueToShortcut, filterShortcutsByMaxDuration, multiSpaceToOne, isSpecialTime, parseSpecialTime, computedSpecialList, MAX_DURATION, } from '../../util';
const GlobalPicker = (_a) => {
    var { hasLiveTail = false, value = '-1h', format = 'YYYY-MM-DD HH:mm', shortcutOptions, customShortcuts, reFreshValue = 0, showRefresh = true, refreshOptions, sessionStorageKey = '', dropdownAlign, maxDuration = MAX_DURATION, leftToastPlacement, curToastPlacement, rightToastPlacement, customRefresh, dropdownClassName } = _a, restProps = __rest(_a, ["hasLiveTail", "value", "format", "shortcutOptions", "customShortcuts", "reFreshValue", "showRefresh", "refreshOptions", "sessionStorageKey", "dropdownAlign", "maxDuration", "leftToastPlacement", "curToastPlacement", "rightToastPlacement", "customRefresh", "dropdownClassName"]);
    const DELAY = 360;
    // locale
    const [datePickerLocale] = useLocaleReceiver('DatePicker');
    const isEN = /en/i.test((datePickerLocale === null || datePickerLocale === void 0 ? void 0 : datePickerLocale.lang.locale) || '');
    const globalPickerLocale = (datePickerLocale === null || datePickerLocale === void 0 ? void 0 : datePickerLocale.globalPickerLocale) || enUS.globalPickerLocale;
    // 特殊日期
    // @ts-ignore
    // eslint-disable-next-line dot-notation
    const specialDate = window === null || window === void 0 ? void 0 : window['Special_Date'];
    // 刷新频率选项
    const mergedRefreshOptions = refreshOptions || generateRefreshOptions(globalPickerLocale);
    // 类名前缀,公共
    const { getPrefixCls, getPopupContainer: getContextPopupContainer } = React.useContext(ConfigContext);
    const rootPrefixCls = getPrefixCls();
    const pickerPrefixCls = getPrefixCls('picker');
    const globalPickerPrefixCls = getPrefixCls('global-picker');
    // 获取dom
    const panelDivRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const preBtnRef = React.useRef(null);
    const nextBtnRef = React.useRef(null);
    // 是否已加载
    const [hasMount, setHasMount] = React.useState(false);
    // 刷新频率
    const [mergeReFreshValue, setMergeReFreshValue] = React.useState(0);
    // 前进后退按钮浮层触发操作
    const [buttonAction, setButtonAction] = React.useState(['hover']);
    // 面板是否打开
    const [panelVisibily, setPanelVisibily] = React.useState(false);
    // 当前日期浮层是否打开
    const [curToastVisibily, setCurToastVisibily] = React.useState(false);
    // 前进浮层是否打开
    const [preToastVisibily, setPreToastVisibily] = React.useState(false);
    // 触发更新
    const [update, setUpdate] = React.useState(false);
    // 当前快捷值
    const [curShortcut, setCurShortcut] = React.useState({
        type: GlobalPickerType.shortcut,
        label: globalPickerLocale === null || globalPickerLocale === void 0 ? void 0 : globalPickerLocale.last1hour,
        value: '-1h',
    });
    // 日期快捷值
    const [shortcutLabel, setShortcutLabel] = React.useState(globalPickerLocale === null || globalPickerLocale === void 0 ? void 0 : globalPickerLocale.last1hour);
    // 最近使用列表
    const [recentlyUsed, setRecentlyUsed] = React.useState([]);
    // 是否是Live Tail
    const isLiveTail = React.useMemo(() => curShortcut.value === 'live' && hasLiveTail, [curShortcut.value]);
    // 快捷选项
    const shortcutList = React.useMemo(() => {
        let list = computeShortcutOptions(globalPickerLocale || {});
        if (customShortcuts && Array.isArray(customShortcuts) && customShortcuts.length) {
            list = customShortcuts.map(item => ({
                label: isEN ? item.labelEN : item.labelCN,
                value: item.value,
                type: GlobalPickerType.shortcut,
            }));
        }
        else if (shortcutOptions && Array.isArray(shortcutOptions) && shortcutOptions.length) {
            list = list.filter(opt => shortcutOptions === null || shortcutOptions === void 0 ? void 0 : shortcutOptions.includes(opt.value));
        }
        // 过滤掉大于最大区间的快捷选项
        list = filterShortcutsByMaxDuration(list, maxDuration);
        list = list.filter(opt => hasLiveTail || opt.value !== 'live');
        list = list.concat(computedSpecialList(globalPickerLocale || {}, specialDate, maxDuration));
        return list;
    }, [shortcutOptions, hasLiveTail, customShortcuts, maxDuration, globalPickerLocale]);
    // 回显值
    const inputValue = React.useMemo(() => {
        const list = computeShortcutOptions(globalPickerLocale || {}).concat(computedSpecialList(globalPickerLocale || {}, specialDate));
        let result = shortcutLabel;
        list.forEach(opt => {
            if (shortcutLabel === null || shortcutLabel === void 0 ? void 0 : shortcutLabel.includes('now')) {
                shortcutLabel.split('~').forEach(v => {
                    if (v.trim() === opt.value) {
                        result = opt.label;
                    }
                });
            }
            else if (shortcutLabel === opt.value) {
                result = opt.label;
            }
        });
        return result;
    }, [shortcutLabel, globalPickerLocale]);
    // 缓存中获取最近使用列表
    React.useEffect(() => {
        let list = [];
        if (typeof sessionStorage !== 'undefined' && sessionStorageKey.trim()) {
            list = JSON.parse(sessionStorage.getItem(sessionStorageKey.trim())) || [];
            list.forEach(item => {
                const inCustom = customShortcuts === null || customShortcuts === void 0 ? void 0 : customShortcuts.find(opt => opt.value === item.value);
                const inList = filterShortcutsByMaxDuration(computeShortcutOptions(globalPickerLocale || {}), maxDuration)
                    .concat(computedSpecialList(globalPickerLocale || {}, specialDate, maxDuration))
                    .find(opt => opt.value === item.value);
                if (inCustom) {
                    item.label = isEN ? inCustom.labelEN : inCustom.labelCN;
                }
                else if (inList) {
                    item.label = inList.label;
                }
            });
            setRecentlyUsed(list);
        }
        if (!hasMount) {
            setHasMount(true);
        }
    }, [globalPickerLocale, sessionStorageKey, customShortcuts, maxDuration]);
    React.useEffect(() => {
        if (hasMount) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            updateRecentlyUsed(curShortcut);
        }
    }, [hasMount]);
    // 设置初始日期值
    React.useEffect(() => {
        if (typeof value === 'string' && value.trim()) {
            const opt = shortcutList.find(item => item.value === value) || valueToShortcut(value);
            setCurShortcut(opt);
            setShortcutLabel(opt.label);
        }
    }, [value, shortcutList]);
    // 取消按钮回调
    const handleCancel = () => {
        setPanelVisibily(false);
        setShortcutLabel(curShortcut.label);
    };
    //  处理props.onChange
    const mergedChange = (shortcut) => {
        var _a, _b;
        const dates = isSpecialTime(shortcut.value)
            ? parseSpecialTime(shortcut.value, specialDate)
            : parseShortcutValue(shortcut.value);
        if (dates && dates[0] && dates[1]) {
            const { isEqual, isGreater } = durationText(dates, maxDuration, globalPickerLocale);
            const dateStrings = [dates[0].format(format), dates[1].format(format)];
            (_a = restProps.onChange) === null || _a === void 0 ? void 0 : _a.call(restProps, isEqual || isGreater ? null : dates, isEqual || isGreater ? null : dateStrings, shortcut.type === GlobalPickerType.shortcut || shortcut.type === GlobalPickerType.special, shortcut.value);
        }
        if (shortcut.value === 'live') {
            (_b = restProps.onChange) === null || _b === void 0 ? void 0 : _b.call(restProps, null, null, shortcut.type === GlobalPickerType.shortcut, shortcut.value);
        }
    };
    // 刷新频率回调
    const handleRefresh = (shortcut, refreshTime) => {
        var _a;
        setMergeReFreshValue(refreshTime);
        const dates = isSpecialTime(shortcut.value)
            ? parseSpecialTime(shortcut.value, specialDate)
            : parseShortcutValue(shortcut.value);
        if (dates && dates[0] && dates[1]) {
            (_a = restProps.onRefresh) === null || _a === void 0 ? void 0 : _a.call(restProps, dates, refreshTime);
        }
    };
    // 更新最近使用列表
    function updateRecentlyUsed(shortcut) {
        let newList = recentlyUsed.slice().filter(item => item.label !== shortcut.label);
        if (newList.length > 9) {
            newList.pop();
        }
        newList.unshift(shortcut);
        // 过滤掉大于最大区间的快捷选项
        newList = filterShortcutsByMaxDuration(newList, maxDuration);
        setRecentlyUsed(newList);
        if (sessionStorageKey.trim() && typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(sessionStorageKey, JSON.stringify(newList));
        }
    }
    // 快捷选项变化回调
    const handleCurShortcut = (shortcut) => {
        setCurShortcut(shortcut);
        setShortcutLabel(shortcut.label);
        updateRecentlyUsed(shortcut);
    };
    // debounce
    const debounceSetCurShortcut = React.useCallback(debounce((v) => {
        setUpdate(v !== curShortcut.value);
        const shortcut = valueToShortcut(v);
        setCurShortcut(shortcut);
    }, DELAY), [recentlyUsed]);
    // input输入回调
    const handleInputChange = e => {
        const v = multiSpaceToOne(e.target.value || '');
        setShortcutLabel(v);
        debounceSetCurShortcut(v);
    };
    // 回车回调
    const handlePressEnter = () => {
        setPanelVisibily(false);
    };
    // 失焦回调
    const handleInputBlur = () => {
        const dates = isSpecialTime(curShortcut.value)
            ? parseSpecialTime(curShortcut.value, specialDate)
            : parseShortcutValue(curShortcut.value);
        const { isEqual, isGreater, isInvalid } = durationText(dates, maxDuration, globalPickerLocale);
        setTimeout(() => {
            // 不能大于时间区间，不能是相等时间
            if (isInvalid || isEqual || isGreater) {
                const first = recentlyUsed[0];
                if (first) {
                    setCurShortcut(first);
                    setShortcutLabel(first.value);
                    setUpdate(false);
                }
            }
        }, DELAY);
    };
    // 键盘事件
    // const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    //   if (e.defaultPrevented) {
    //     return; // 如果事件已经在进行中，则不做任何事。
    //   }
    //   switch (e.key) {
    //     case 'Enter':
    //       // 按“回车”键时要做的事。
    //       mergedChange(curShortcut);
    //       setPanelVisibily(false);
    //       break;
    //     case 'Escape':
    //       // 按“ESC”键时要做的事。
    //       setShortcutValue(preShortcut.current.label);
    //       setCurShortcut(preShortcut.current);
    //       setPanelVisibily(false);
    //       break;
    //     default:
    //       return; // 什么都没按就退出吧。
    //   }
    //   // 取消默认动作，从而避免处理两次。
    //   e.preventDefault();
    // };
    // 关闭浮层，触发props.onchange
    const triggerChange = (shortcut) => {
        setTimeout(() => {
            const dates = isSpecialTime(shortcut.value)
                ? parseSpecialTime(shortcut.value, specialDate)
                : parseShortcutValue(shortcut.value);
            const { isEqual, isGreater, isInvalid } = durationText(dates, maxDuration, globalPickerLocale);
            // 不能大于时间区间，不能是相等时间 or 是liveState
            if (!(isInvalid || isEqual || isGreater) || shortcut.value === 'live') {
                mergedChange(shortcut);
            }
        }, 100);
    };
    // div点击回调
    const handleTextClick = () => {
        var _a, _b;
        if (restProps.timeDisabled)
            return;
        setPanelVisibily(true);
        if (curShortcut.type === GlobalPickerType.shortcut) {
            setShortcutLabel(curShortcut.value);
        }
        else if (curShortcut.type === GlobalPickerType.custom) {
            setShortcutLabel(curShortcut === null || curShortcut === void 0 ? void 0 : curShortcut.label);
        }
        else if (curShortcut.type === GlobalPickerType.special) {
            const dates = parseSpecialTime(curShortcut.value, specialDate);
            setShortcutLabel([(_a = dates === null || dates === void 0 ? void 0 : dates[0]) === null || _a === void 0 ? void 0 : _a.format(format), (_b = dates === null || dates === void 0 ? void 0 : dates[1]) === null || _b === void 0 ? void 0 : _b.format(format)].join(' ~ '));
        }
    };
    // 计算上一个时间范围
    const computedPreDate = (shortcut) => {
        const dates = isSpecialTime(shortcut.value)
            ? parseSpecialTime(shortcut.value, specialDate)
            : parseShortcutValue(shortcut.value);
        if (dates && dates[0] && dates[1]) {
            return [dates[0].subtract(computedDuration(dates).ms, 'ms'), dates[0]];
        }
        return null;
    };
    // 计算下一个时间范围
    const computedNextDate = (shortcut) => {
        const dates = isSpecialTime(shortcut.value)
            ? parseSpecialTime(shortcut.value, specialDate)
            : parseShortcutValue(shortcut.value);
        if (dates && dates[0] && dates[1]) {
            const end = dates[1].add(computedDuration(dates).ms, 'ms');
            return [dates[1], end];
        }
        return null;
    };
    // 下一个时间范围是否在当前时间之后
    const nextDateIsAfterNow = (v) => {
        const nextDate = computedNextDate(v);
        return nextDate && nextDate[1] && nextDate[1].isAfter(dayjs());
    };
    // 时间后退
    const handlePreTime = (shortcut) => {
        const preDate = computedPreDate(shortcut);
        // const preValue = datesToValue(preDate);
        const preDateString = preDate === null || preDate === void 0 ? void 0 : preDate.map((v) => v === null || v === void 0 ? void 0 : v.format(format));
        const current = {
            type: GlobalPickerType.custom,
            label: preDateString.join(' ~ '),
            value: preDateString.join(' ~ '),
        };
        handleCurShortcut(current);
        triggerChange(current);
        setPreToastVisibily(shortcut.type === GlobalPickerType.custom);
        if (!panelVisibily && shortcut.type !== GlobalPickerType.custom) {
            setCurToastVisibily(true);
        }
    };
    // 时间前进
    const handleNextTime = (shortcut) => {
        const nextDate = computedNextDate(shortcut);
        if (nextDate && nextDate[1] && nextDate[1].isAfter(dayjs())) {
            return;
        }
        const nextDateString = nextDate === null || nextDate === void 0 ? void 0 : nextDate.map((v) => v === null || v === void 0 ? void 0 : v.format(format));
        const current = {
            type: GlobalPickerType.custom,
            label: nextDateString.join(' ~ '),
            value: nextDateString.join(' ~ '),
        };
        handleCurShortcut(current);
        triggerChange(current);
    };
    // 清除按钮
    const handleClear = (e) => {
        e.stopPropagation();
        setShortcutLabel('');
    };
    // Global click handler
    React.useEffect(() => addGlobalMouseDownEvent((e) => {
        var _a;
        const target = getTargetFromEvent(e);
        if (panelVisibily) {
            const clickedOutside = !elementsContains([
                panelDivRef.current,
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.input,
                preBtnRef.current,
                nextBtnRef.current,
                // textDivRef.current,
            ], target);
            // if (!clickedOutside) {
            //   preventBlurRef.current = true;
            //   // Always set back in case `onBlur` prevented by user
            //   requestAnimationFrame(() => {
            //     preventBlurRef.current = false;
            //   });
            // } else
            if (clickedOutside) {
                handleCancel();
            }
        }
    }));
    // 变更前进 后退浮窗的开启方式
    React.useEffect(() => {
        var _a, _b;
        if (panelVisibily) {
            setButtonAction([]);
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus({
                cursor: 'end',
            });
        }
        else {
            setButtonAction(['hover']);
        }
        (_b = restProps === null || restProps === void 0 ? void 0 : restProps.onOpenChange) === null || _b === void 0 ? void 0 : _b.call(restProps, panelVisibily, 'panel');
    }, [panelVisibily]);
    // 刷新频率受控
    React.useEffect(() => {
        setMergeReFreshValue(reFreshValue);
    }, [reFreshValue]);
    React.useEffect(() => {
        if (!panelVisibily && update) {
            triggerChange(curShortcut);
            setUpdate(false);
        }
    }, [panelVisibily, update]);
    // 面板
    const panelNode = (<div className={classNames(`${pickerPrefixCls}-panel-container`, globalPickerPrefixCls)} ref={panelDivRef}>
      <Panel visibily={panelVisibily} onClose={() => setPanelVisibily(false)} onCancle={handleCancel} onCurShortcutChange={handleCurShortcut} setUpdate={setUpdate} format={format} locale={globalPickerLocale} curShortcut={curShortcut} shortcutList={shortcutList} recentlyUsed={recentlyUsed} maxDuration={maxDuration} hasLiveTail={hasLiveTail} isLiveTail={isLiveTail} pickerPrefixCls={pickerPrefixCls} {...restProps}/>
    </div>);
    // 浮层
    const renderToast = (type, shortcut) => {
        var _a;
        let result = isSpecialTime(shortcut.value)
            ? parseSpecialTime(shortcut.value, specialDate)
            : parseShortcutValue(shortcut.value);
        let isAfterNow = false;
        let timeRangeText = globalPickerLocale === null || globalPickerLocale === void 0 ? void 0 : globalPickerLocale.timeRange;
        if (type === 'pre') {
            timeRangeText = globalPickerLocale === null || globalPickerLocale === void 0 ? void 0 : globalPickerLocale.preTimeRange;
            result = computedPreDate(shortcut);
        }
        else if (type === 'next') {
            timeRangeText = globalPickerLocale === null || globalPickerLocale === void 0 ? void 0 : globalPickerLocale.nextTimeRange;
            result = computedNextDate(shortcut);
            if (result && result[1] && result[1].isAfter(dayjs())) {
                isAfterNow = true;
            }
        }
        if (result && result[0] && result[1]) {
            const { isInvalid, isEqual, isGreater, formatString } = durationText(result, maxDuration, globalPickerLocale);
            return (<div className={classNames(`${globalPickerPrefixCls}-toast`, {
                    [`${globalPickerPrefixCls}-toast-error`]: isAfterNow || isInvalid || isEqual || isGreater,
                })}>
          <div className={`${globalPickerPrefixCls}-toast-title`}>{timeRangeText}</div>
          <div className={`${globalPickerPrefixCls}-toast-content-wrap`}>
            <div className={`${globalPickerPrefixCls}-toast-dot`}>
              <span className={`${globalPickerPrefixCls}-toast-dot-start`}/>
              <span className={`${globalPickerPrefixCls}-toast-dot-dash`}/>
              <span className={`${globalPickerPrefixCls}-toast-dot-end`}/>
            </div>
            <div className={`${globalPickerPrefixCls}-toast-content`}>
              <div className={`${globalPickerPrefixCls}-toast-content-start`}>
                {result[0] ? result[0].format(format) : ''}
              </div>
              <div className={`${globalPickerPrefixCls}-toast-content-duration`}>
                {isAfterNow ? globalPickerLocale === null || globalPickerLocale === void 0 ? void 0 : globalPickerLocale.invalidTimeRange : formatString}
              </div>
              <div className={`${globalPickerPrefixCls}-toast-content-end`}>
                {isAfterNow ? globalPickerLocale === null || globalPickerLocale === void 0 ? void 0 : globalPickerLocale.noFuture : (_a = result[1]) === null || _a === void 0 ? void 0 : _a.format(format)}
              </div>
            </div>
          </div>
        </div>);
        }
        return null;
    };
    return (<div className={classNames(`${globalPickerPrefixCls}-wrap`)}>
      <div className={classNames(`${rootPrefixCls}-picker`, {
            [`${rootPrefixCls}-picker-focused`]: panelVisibily,
            [`${rootPrefixCls}-picker-open`]: panelVisibily || isLiveTail,
            [`${rootPrefixCls}-picker-disabled`]: restProps.timeDisabled,
        })}>
        {isLiveTail ? null : (<Trigger action={buttonAction} popup={renderToast('pre', curShortcut)} popupPlacement={leftToastPlacement} popupClassName={dropdownClassName} popupVisible={preToastVisibily} getPopupContainer={restProps.getPopupContainer || getContextPopupContainer} onPopupVisibleChange={visible => setPreToastVisibily(visible)}>
            <Button disabled={restProps.timeDisabled} type="text" ref={preBtnRef} icon={<ControlOpen style={{ transform: 'rotate(90deg)' }}/>} className={`${globalPickerPrefixCls}-left-arrow`} onClick={() => handlePreTime(curShortcut)}/>
          </Trigger>)}
        <Trigger popupPlacement={isLiveTail ? 'bottomRight' : 'bottomRight30'} popup={panelNode} popupVisible={panelVisibily} popupClassName={dropdownClassName} getPopupContainer={restProps.getPopupContainer || getContextPopupContainer} popupAlign={dropdownAlign}>
          <Trigger popupPlacement={curToastPlacement || 'bottomLeft5'} popup={renderToast('cur', curShortcut)} popupVisible={curToastVisibily} popupClassName={dropdownClassName} getPopupContainer={restProps.getPopupContainer || getContextPopupContainer}>
            <div className={classNames(`${globalPickerPrefixCls}-text-wrap`, {
            disabled: restProps.timeDisabled,
        })}>
              {isLiveTail ? (<>
                  <div className={`${globalPickerPrefixCls}-live`} onClick={handleTextClick}>
                    <span className={`${globalPickerPrefixCls}-live-icon`}>
                      <LiveTail />
                    </span>
                    <span>Live Tail</span>
                  </div>
                  <ControlOpen className={classNames(`${globalPickerPrefixCls}-open`, {
                open: panelVisibily,
            })}/>
                </>) : (<>
                  <Input ref={inputRef} value={shortcutLabel} className={classNames(`${globalPickerPrefixCls}-input`, {
                hidden: !panelVisibily,
            })} placeholder="-1h30m，-5M，now/d" bordered={false} onChange={handleInputChange} onPressEnter={handlePressEnter} onBlur={handleInputBlur}/>
                  <ControlCloseCircle onMouseDown={handleClear} className={classNames(`${globalPickerPrefixCls}-clear`, {
                hidden: !panelVisibily,
            })}/>
                  <div className={classNames(`${globalPickerPrefixCls}-text`, {
                hidden: panelVisibily,
            })} onClick={handleTextClick} onMouseEnter={() => setCurToastVisibily(true)} onMouseLeave={() => setCurToastVisibily(false)}>
                    {inputValue}
                  </div>
                </>)}
            </div>
          </Trigger>
        </Trigger>
        {isLiveTail ? null : (<Trigger action={buttonAction} popup={renderToast('next', curShortcut)} popupPlacement={rightToastPlacement} popupClassName={dropdownClassName} getPopupContainer={restProps.getPopupContainer || getContextPopupContainer}>
            <Button disabled={restProps.timeDisabled} type="text" ref={nextBtnRef} icon={<ControlOpen style={{ transform: 'rotate(-90deg)' }}/>} className={classNames(`${globalPickerPrefixCls}-right-arrow`, {
                [`${globalPickerPrefixCls}-disabled-icon`]: nextDateIsAfterNow(curShortcut),
            })} onClick={() => handleNextTime(curShortcut)}/>
          </Trigger>)}
      </div>
      {customRefresh ? customRefresh === null || customRefresh === void 0 ? void 0 : customRefresh(isLiveTail) : null}
      {showRefresh && !isLiveTail ? (<Select className={`${globalPickerPrefixCls}-select`} options={mergedRefreshOptions} 
        // defaultValue={reFreshValue}
        value={mergeReFreshValue} onChange={v => handleRefresh(curShortcut, v)} disabled={restProps.refreshDisabled || curShortcut.type === GlobalPickerType.custom} getPopupContainer={restProps.getPopupContainer || getContextPopupContainer} style={{ width: isEN ? 112 : 88, marginLeft: 8 }} onDropdownVisibleChange={visible => { var _a; return (_a = restProps === null || restProps === void 0 ? void 0 : restProps.onOpenChange) === null || _a === void 0 ? void 0 : _a.call(restProps, visible, 'refresh'); }}/>) : null}
    </div>);
};
export default GlobalPicker;
